import { Link } from '../../components/Link.tsx';
import { Hero } from '../../components/marketing/Hero.tsx';
import { MarketingFooter } from '../../components/marketing/MarketingFooter.tsx';
import { CrossLinks } from '../../components/marketing/CrossLinks.tsx';
import { MediaVideo } from '../../components/MediaVideo.tsx';
import { useDocumentTitle } from '../../hooks/useDocumentTitle.ts';
import { useT } from '../../i18n.ts';
import '../../styles/marketing.css';

export function FeatureApprovals() {
  const t = useT();
  useDocumentTitle(t('features-approvals.document-title'));

  return (
    <div className="mkt-page">
      <Hero
        eyebrow={t('features-approvals.hero.eyebrow')}
        title={t('features-approvals.hero.title')}
        lede={t('features-approvals.hero.lede')}
      >
        <Link to="/register" className="mkt-btn primary lg">
          {t('features-approvals.hero.cta-try')}
        </Link>
        <Link to="/dashboard" className="mkt-btn ghost lg">
          {t('marketing.cta.login')}
        </Link>
      </Hero>

      {/* Overview walkthrough video */}
      <section className="mkt-section sunk">
        <div className="mkt-container">
          <div className="mkt-section-head">
            <span className="mkt-eyebrow">{t('features-approvals.overview.eyebrow')}</span>
            <h2 className="mkt-h2">{t('features-approvals.overview.heading')}</h2>
            <p className="mkt-section-lede">
              {t('features-approvals.overview.lede')}
            </p>
          </div>
          <div style={{ maxWidth: '760px', margin: '0 auto' }}>
            <MediaVideo
              base="overview-approval-rules-software"
              label={t('features-approvals.overview.video-label')}
            />
          </div>
        </div>
      </section>

      {/* Named, composable rules */}
      <section className="mkt-section">
        <div className="mkt-container">
          <div className="mkt-split">
            <div className="mkt-split-body">
              <span className="mkt-eyebrow">{t('features-approvals.named-rules.eyebrow')}</span>
              <h3>{t('features-approvals.named-rules.heading')}</h3>
              <p>{t('features-approvals.named-rules.body-1')}</p>
              <p>{t('features-approvals.named-rules.body-2')}</p>
              <p>
                <Link to="/docs/approval-rules" className="mkt-inline-link">
                  {t('features-approvals.named-rules.docs-link')}
                </Link>
              </p>
            </div>
            <div className="mkt-rule-stack">
              <div className="mkt-rule-expr" aria-hidden="true">
                <span className="mkt-rule-expr-eyebrow">
                  {t('features-approvals.named-rules.expr-eyebrow')}
                </span>
                <h3>Home release</h3>
                <div className="mkt-rule-expr-conditions">
                  <span className="mkt-rule-chip">Chief Product Officer</span>
                  <span className="mkt-rule-join">and</span>
                  <span className="mkt-rule-chip">VP Engineering</span>
                  <span className="mkt-rule-join">and</span>
                  <span className="mkt-rule-chip">Chief Design Officer</span>
                  <span className="mkt-rule-join">and</span>
                  <span className="mkt-rule-chip">Home - Product</span>
                  <span className="mkt-rule-join">and</span>
                  <span className="mkt-rule-chip">Home - Eng</span>
                  <span className="mkt-rule-join">and</span>
                  <span className="mkt-rule-chip ref">Design review</span>
                </div>
                <p className="mkt-rule-expr-note">
                  {t('features-approvals.named-rules.expr-note')}
                </p>
              </div>
              <div className="mkt-rule-expr compact" aria-hidden="true">
                <span className="mkt-rule-expr-eyebrow">
                  {t('features-approvals.named-rules.or-eyebrow')}
                </span>
                <h4>Platform review</h4>
                <div className="mkt-rule-expr-conditions">
                  <span className="mkt-rule-chip">Home - Eng</span>
                  <span className="mkt-rule-join or">or</span>
                  <span className="mkt-rule-chip">Growth - Eng</span>
                </div>
                <p className="mkt-rule-expr-note">
                  {t('features-approvals.named-rules.or-note')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Team-level quorum */}
      <section className="mkt-section sunk">
        <div className="mkt-container">
          <div className="mkt-section-head">
            <span className="mkt-eyebrow">{t('features-approvals.team-quorum.eyebrow')}</span>
            <h2 className="mkt-h2">{t('features-approvals.team-quorum.heading')}</h2>
            <p className="mkt-section-lede">
              {t('features-approvals.team-quorum.lede')}
            </p>
          </div>
          <div className="mkt-team-cards">
            <div className="mkt-team-card">
              <div className="mkt-team-card-stripe blue" />
              <div className="mkt-team-card-body">
                <h4>App Design Team</h4>
                <div className="mkt-team-card-meta">3 members</div>
                <div className="mkt-team-card-roster">Kofi · Mei · Sam</div>
                <div className="mkt-team-card-rule-label">Quorum</div>
                <div className="mkt-team-card-rule">Any one member</div>
                <div className="mkt-team-card-note">
                  Any designer can review, so design sign-off never waits on one person.
                </div>
              </div>
            </div>
            <div className="mkt-team-card">
              <div className="mkt-team-card-stripe amber" />
              <div className="mkt-team-card-body">
                <h4>Home - Eng</h4>
                <div className="mkt-team-card-meta">3 members</div>
                <div className="mkt-team-card-roster">Mike · David · Omar</div>
                <div className="mkt-team-card-rule-label">Quorum</div>
                <div className="mkt-team-card-rule">At least 2 of 3</div>
                <div className="mkt-team-card-note">
                  Two signers keep rigor without blocking on any single engineer.
                </div>
              </div>
            </div>
            <div className="mkt-team-card">
              <div className="mkt-team-card-stripe navy" />
              <div className="mkt-team-card-body">
                <h4>Home - Product</h4>
                <div className="mkt-team-card-meta">2 members</div>
                <div className="mkt-team-card-roster">Jenna · Nadia</div>
                <div className="mkt-team-card-rule-label">Quorum</div>
                <div className="mkt-team-card-rule">All members</div>
                <div className="mkt-team-card-note">
                  Both PMs own the Home spec, so both have to agree.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Scoped approval — accumulation */}
      <section className="mkt-section">
        <div className="mkt-container">
          <div className="mkt-section-head">
            <span className="mkt-eyebrow">{t('features-approvals.scoped.eyebrow')}</span>
            <h2 className="mkt-h2">{t('features-approvals.scoped.heading')}</h2>
            <p className="mkt-section-lede">
              {t('features-approvals.scoped.lede')}
            </p>
          </div>
          <div className="mkt-scope-compare">
            <div className="mkt-scope-card">
              <div className="mkt-scope-head">
                <span className="mkt-scope-tag">{t('features-approvals.scoped.change-a.tag')}</span>
                <span className="mkt-scope-what">{t('features-approvals.scoped.change-a.what')}</span>
              </div>
              <div className="mkt-scope-rules">
                <div className="mkt-scope-rule">
                  <strong>Growth review</strong>
                  <span>Growth - Product</span>
                </div>
              </div>
              <div className="mkt-scope-foot">{t('features-approvals.scoped.change-a.foot')}</div>
            </div>
            <div className="mkt-scope-card">
              <div className="mkt-scope-head">
                <span className="mkt-scope-tag">{t('features-approvals.scoped.change-b.tag')}</span>
                <span className="mkt-scope-what">{t('features-approvals.scoped.change-b.what')}</span>
              </div>
              <div className="mkt-scope-rules">
                <div className="mkt-scope-rule">
                  <strong>Growth review</strong>
                  <span>Growth - Product</span>
                </div>
                <div className="mkt-scope-rule added">
                  <strong>Design review</strong>
                  <span>App Design Team</span>
                  <span className="mkt-scope-why">{t('features-approvals.scoped.why-tag')}</span>
                </div>
              </div>
              <div className="mkt-scope-foot">{t('features-approvals.scoped.change-b.foot')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* Real-time approval tracking */}
      <section className="mkt-section sunk">
        <div className="mkt-container">
          <div className="mkt-split">
            <div className="mkt-mock" aria-hidden="true">
              <div className="mkt-mock-head">
                <span className="mkt-mock-head-eyebrow">Home release</span>
                <span className="mkt-mock-head-title">v48 — Home dashboard redesign</span>
                <span className="mkt-mock-head-tag">4 of 6 satisfied</span>
              </div>
              <div className="mkt-mock-body">
                <div className="mkt-approval-list">
                  <div className="mkt-approval-row">
                    <span className="mkt-approval-dot done">✓</span>
                    <div>
                      <div className="mkt-approval-name">Chief Product Officer</div>
                      <div className="mkt-approval-quorum">Any one member</div>
                    </div>
                    <span className="mkt-approval-status satisfied">Sara ✓</span>
                  </div>
                  <div className="mkt-approval-row">
                    <span className="mkt-approval-dot done">✓</span>
                    <div>
                      <div className="mkt-approval-name">Home - Product</div>
                      <div className="mkt-approval-quorum">All members</div>
                    </div>
                    <span className="mkt-approval-status satisfied">Jenna ✓ · Nadia ✓</span>
                  </div>
                  <div className="mkt-approval-row">
                    <span className="mkt-approval-dot done">✓</span>
                    <div>
                      <div className="mkt-approval-name">Home - Eng</div>
                      <div className="mkt-approval-quorum">At least 2 of 3</div>
                    </div>
                    <span className="mkt-approval-status satisfied">Mike ✓ · David ✓</span>
                  </div>
                  <div className="mkt-approval-row">
                    <span className="mkt-approval-dot done">✓</span>
                    <div>
                      <div className="mkt-approval-name">App Design Team</div>
                      <div className="mkt-approval-quorum">Any member · via Design review</div>
                    </div>
                    <span className="mkt-approval-status satisfied">Kofi ✓</span>
                  </div>
                  <div className="mkt-approval-row">
                    <span className="mkt-approval-dot pending">○</span>
                    <div>
                      <div className="mkt-approval-name">VP Engineering</div>
                      <div className="mkt-approval-quorum">Sole approver</div>
                    </div>
                    <span className="mkt-approval-status">awaiting Jared</span>
                  </div>
                  <div className="mkt-approval-row">
                    <span className="mkt-approval-dot pending">○</span>
                    <div>
                      <div className="mkt-approval-name">Chief Design Officer</div>
                      <div className="mkt-approval-quorum">Sole approver</div>
                    </div>
                    <span className="mkt-approval-status">awaiting Priya</span>
                  </div>
                </div>
              </div>
            </div>
            <div className="mkt-split-body">
              <span className="mkt-eyebrow">{t('features-approvals.realtime.eyebrow')}</span>
              <h3>{t('features-approvals.realtime.heading')}</h3>
              <p>
                {t('features-approvals.realtime.body-1')}
              </p>
              <p>
                {t('features-approvals.realtime.body-2')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Actions: approve, reject, withdraw */}
      <section className="mkt-section">
        <div className="mkt-container">
          <div className="mkt-section-head">
            <span className="mkt-eyebrow">{t('features-approvals.lifecycle.eyebrow')}</span>
            <h2 className="mkt-h2">{t('features-approvals.lifecycle.heading')}</h2>
          </div>
          <div className="mkt-pillars">
            <div className="mkt-pillar">
              <span className="mkt-pillar-number">{t('features-approvals.lifecycle.submit.label')}</span>
              <h3 className="mkt-pillar-title">{t('features-approvals.lifecycle.submit.title')}</h3>
              <p className="mkt-pillar-body">
                {t('features-approvals.lifecycle.submit.body')}
              </p>
            </div>
            <div className="mkt-pillar">
              <span className="mkt-pillar-number">{t('features-approvals.lifecycle.approve.label')}</span>
              <h3 className="mkt-pillar-title">{t('features-approvals.lifecycle.approve.title')}</h3>
              <p className="mkt-pillar-body">
                {t('features-approvals.lifecycle.approve.body')}
              </p>
            </div>
            <div className="mkt-pillar">
              <span className="mkt-pillar-number">{t('features-approvals.lifecycle.reject.label')}</span>
              <h3 className="mkt-pillar-title">{t('features-approvals.lifecycle.reject.title')}</h3>
              <p className="mkt-pillar-body">
                {t('features-approvals.lifecycle.reject.body')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Audit trail */}
      <section className="mkt-section sunk">
        <div className="mkt-container">
          <div className="mkt-section-head">
            <span className="mkt-eyebrow">{t('features-approvals.audit.eyebrow')}</span>
            <h2 className="mkt-h2">{t('features-approvals.audit.heading')}</h2>
            <p className="mkt-section-lede">
              {t('features-approvals.audit.lede')}
            </p>
            <p className="mkt-section-lede">
              {t('features-approvals.audit.body')}
            </p>
          </div>
        </div>
      </section>

      {/* Docs reference */}
      <section className="mkt-section">
        <div className="mkt-container">
          <div className="mkt-section-head">
            <span className="mkt-eyebrow">{t('features-approvals.docs.eyebrow')}</span>
            <h2 className="mkt-h2">{t('features-approvals.docs.heading')}</h2>
            <p className="mkt-section-lede">
              {t('features-approvals.docs.body')}
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div className="mkt-cta-row">
              <Link to="/docs/approval-rules" className="mkt-btn ghost lg">
                {t('features-approvals.docs.cta')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-links */}
      <section className="mkt-section sunk">
        <div className="mkt-container">
          <div className="mkt-section-head">
            <h2 className="mkt-h2">{t('marketing.cta.keep-reading')}</h2>
          </div>
          <CrossLinks
            links={[
              {
                to: '/features/documents',
                eyebrow: t('marketing.feature.documents.eyebrow'),
                title: t('marketing.feature.documents.title'),
                desc: t('features-approvals.crosslink.documents.desc'),
              },
              {
                to: '/features/ai',
                eyebrow: t('marketing.feature.ai.eyebrow'),
                title: t('marketing.feature.ai.title'),
                desc: t('features-approvals.crosslink.ai.desc'),
              },
            ]}
          />
        </div>
      </section>

      {/* CTA */}
      <section className="mkt-cta-strip">
        <div className="mkt-container-narrow">
          <h2 className="mkt-h2">{t('features-approvals.cta.heading')}</h2>
          <p className="mkt-section-lede">
            {t('features-approvals.cta.lede')}
          </p>
          <div className="mkt-cta-row">
            <Link to="/register" className="mkt-btn primary lg">
              {t('marketing.cta.signup-free')}
            </Link>
            <Link to="/dashboard" className="mkt-btn ghost lg">
              {t('marketing.cta.login')}
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
}
